import React from 'react';
import { postDeploy } from 'Services';
import { useTranslation, Trans } from 'react-i18next';
import { RQ_LATEST_DEPLOY_PROCESS, getDeployCount, getDeployCountString } from 'Utils';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type Props = {
    onSuccess?: (data: DeploymentResponse) => void;
    onError?: () => void;
};

const useMutateDeploy = (templateRepositoryId: number, { onSuccess, onError }: Props) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { addModal, addTechnicalErrorModal } = useReduxModal();
    return useMutation(postDeploy, {
        onSuccess: (data) => {
            queryClient.invalidateQueries([RQ_LATEST_DEPLOY_PROCESS, templateRepositoryId]);
            const deploymentCount = getDeployCount(data.summary);
            const deploymentCountString = getDeployCountString(data.summary);

            const content = (
                <>
                    <Trans i18nKey='deployHasStarted' count={deploymentCount}>
                        Your deployment of <strong>{{ deploymentCountString }} updates</strong> may take a while to
                        complete. You will be notified when the deployment is live.
                    </Trans>
                    <div style={{ marginTop: '24px' }}>{t('Close this message to return to the sandbox')}</div>
                </>
            );
            addModal({
                id: 'deploy-success',
                status: 'success',
                title: t('Deployment has started'),
                content,
                cancelText: t('Close')
            });

            onSuccess?.(data);
        },
        onError: (err: any) => {
            if (err?.status === 409) {
                queryClient.invalidateQueries([RQ_LATEST_DEPLOY_PROCESS, templateRepositoryId]);
                addModal({
                    id: 'deploy-warning',
                    status: 'warning',
                    title: t('Deployment is in progress'),
                    content: t('A deployment is currently in progress'),
                    cancelText: t('Close')
                });
                onError?.();
            } else {
                addTechnicalErrorModal(t('Failed to make deployment'), 'Unable to deploy changes');
            }
        }
    });
};

export default useMutateDeploy;
